/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';

import {
  colorSelectionMasterFinderDialogRoute,
  CSMasterFinderDialogConfig,
  CSMasterFinderListItem,
  getCSMasterFinderListItemId,
  getHousePlanId,
  getJobSpecTemplateFinderListItemId,
  HousePlanFinderDialogConfig,
  housePlanFinderDialogRoute,
  HousePlanFinderListItem,
  JobSpecTemplateFinderDialogConfig,
  jobSpecTemplateFinderDialogRoute,
  JobSpecTemplateFinderListItem,
  ScheduleTemplateFinderDialogConfig,
  scheduleTemplateFinderDialogRoute,
} from '@gh/builder-shared';
import { SentToOfficeEntryPoint } from '@gh/builder-shared-data';
import { FormState, FormStateBuilder, getAllNewSelectedEntities } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  getControlValue,
  NotificationService,
  setControlValue,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';

import { isNil } from 'lodash';

import { ExportOfficeBusinessService } from '../resources/office-export-business.service';
import {
  JobSpecTemplateListItem,
  OfficeListItem,
  ScheduleTemplateListItem,
  SourceType,
} from '../resources/office-export.model';
import { CSMasterExportGridDefinition } from './office-export-cs-master-grid-definition.service';
import { HpExportGridDefinition } from './office-export-hp-grid-definition.service';
import { JobSpecTemplateGridDefinition } from './office-export-job-spec-tempate-grid-definition.service';
import { ScheduleTemplateGridDefinition } from './office-export-schedule-template-grid-definition.service';

@Component({
  selector: 'gh-export-house-plans', // renaming need
  styles: [require('./office-export.component.scss')],
  template: require('./office-export.component.html'),
  viewProviders: [
    HpExportGridDefinition,
    CSMasterExportGridDefinition,
    JobSpecTemplateGridDefinition,
    ScheduleTemplateGridDefinition,
  ],
})
export class ExportOfficeComponent implements OnInit {

  form: FormState<any>;

  hpGridData: HousePlanFinderListItem[] = [];
  cSMasterGridData: CSMasterFinderListItem[] = [];
  jobSpecTemplateGridData: JobSpecTemplateFinderListItem[] = [];
  scheduleTemplatesGridData: ScheduleTemplateListItem[] = [];

  sourceType: SourceType = SourceType.HousePlans;

  constructor(public hpGridDefinition: HpExportGridDefinition,
              public cSMasterGridDefinition: CSMasterExportGridDefinition,
              public jobSpecTemplateGridDefinition: JobSpecTemplateGridDefinition,
              public scheduleTemplatesGridDefinition: ScheduleTemplateGridDefinition,
              private formStateBuilder: FormStateBuilder,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private businessService: ExportOfficeBusinessService,
              private translation: TranslationService,
              private waitingService: WaitingService) {
  }

  ngOnInit(): void {
    this.form = this.formStateBuilder.buildPlain({
      value: {
        fromOffice: void 0,
        toOffice: void 0,
        createNew: false,
      },
      children: {
        fromOffice: void 0,
        toOffice: void 0,
        createNew: void 0,
      },
    });
  }

  get isCreateNewVisible(): boolean {
    return this.sourceType === SourceType.HousePlans;
  }

  get isDisabledSendButton(): boolean {
    return (isNil(this.fromOffice) || isNil(this.toOffice) ||
      (this.isHpSource && this.hpGridData.length === 0) ||
      (this.isCSMasterSource && this.cSMasterGridData.length === 0) ||
      (this.isScheduleTemplatesSource && this.scheduleTemplatesGridData.length === 0) ||
      (this.isJobSpecTemplateSource && this.jobSpecTemplateGridData.length === 0));
  }

  get isDisabledAddButton(): boolean {
    return isNil(this.fromOffice);
  }

  get fromOffice(): OfficeListItem {
    return getControlValue(this.form.group, 'fromOffice');
  }

  get toOffice(): OfficeListItem {
    return getControlValue(this.form.group, 'toOffice');
  }

  get createNew(): boolean {
    return getControlValue(this.form.group, 'createNew');
  }

  get isHpSource(): boolean {
    return this.sourceType === SourceType.HousePlans;
  }

  get isCSMasterSource(): boolean {
    return this.sourceType === SourceType.CSMaster;
  }

  get isJobSpecTemplateSource(): boolean {
    return this.sourceType === SourceType.JobSpecTemplates;
  }

  get isScheduleTemplatesSource(): boolean {
    return this.sourceType === SourceType.ScheduleTemplates;
  }

  get tableTitle(): string {
    switch (this.sourceType) {
      case SourceType.CSMaster:
        return this.translation.get('lbl_color_selection_masters');
      case SourceType.HousePlans:
        return this.translation.get('lbl_house_plans');
      case SourceType.JobSpecTemplates:
        return this.translation.get('lbl_specification_templates');
      case SourceType.ScheduleTemplates:
        return this.translation.get('lbl_schedule_templates');
      default:
        return '';
    }
  }

  onEditFromOffice(): void {
    this.hpGridData = [];
    this.cSMasterGridData = [];
    this.jobSpecTemplateGridData = [];
    this.scheduleTemplatesGridData = [];
    setControlValue(this.form.group, 'toOffice', void 0);
  }

  sendHP(): void {
    this.businessService.sendToOffice({
      sourceOfficeId: this.fromOffice.officeId,
      destinationOfficeId: this.toOffice.officeId,
      isCreateNew: this.createNew,
      itemIds: this.hpGridData.map(({ housePlanId }: HousePlanFinderListItem) => housePlanId),
      entryPoint: SentToOfficeEntryPoint.HP,
    }).pipe(this.waitingService.operator())
      .subscribe((res) => {
        this.hpGridData = [];
        this.form.group.reset();
        this.notificationService.success('msg_send_tasks_added');
      },         this.notificationService.processAll);
  }

  sendCSMaster(): void {
    this.businessService.sendToOffice({
      sourceOfficeId: this.fromOffice.officeId,
      destinationOfficeId: this.toOffice.officeId,
      isCreateNew: this.createNew,
      itemIds: this.cSMasterGridData.map(({ id }: CSMasterFinderListItem) => id),
      entryPoint: SentToOfficeEntryPoint.CSMasters,
    }).pipe(this.waitingService.operator())
      .subscribe((res) => {
        this.cSMasterGridData = [];
        this.form.group.reset();
        this.notificationService.success('msg_sent_cs_master_successfully');
      },         this.notificationService.processAll);
  }

  sendJobSpecTemplate(): void {
    this.businessService.sendToOffice({
      sourceOfficeId: this.fromOffice.officeId,
      destinationOfficeId: this.toOffice.officeId,
      isCreateNew: this.createNew,
      itemIds: this.jobSpecTemplateGridData.map(({ id }: JobSpecTemplateListItem) => id),
      entryPoint: SentToOfficeEntryPoint.SP,
    }).pipe(this.waitingService.operator())
      .subscribe((res) => {
        this.jobSpecTemplateGridData = [];
        this.form.group.reset();
        this.notificationService.success('msg_sent_spec_template_successfully');
      },         this.notificationService.processAll);
  }

  sendScheduleTemplate(): void {
    this.businessService.sendToOffice({
      sourceOfficeId: this.fromOffice.officeId,
      destinationOfficeId: this.toOffice.officeId,
      isCreateNew: this.createNew,
      itemIds: this.scheduleTemplatesGridData.map(({ id }: ScheduleTemplateListItem) => id),
      entryPoint: SentToOfficeEntryPoint.SCH,
    }).pipe(this.waitingService.operator())
      .subscribe((res) => {
        this.scheduleTemplatesGridData = [];
        this.form.group.reset();
        this.notificationService.success('msg_sent_sch_template_successfully');
      },         this.notificationService.processAll);
  }

  onSend(): void {
    switch (this.sourceType) {
      case SourceType.HousePlans:
        this.sendHP();
        break;
      case SourceType.CSMaster:
        this.sendCSMaster();
        break;
      case SourceType.JobSpecTemplates:
        this.sendJobSpecTemplate();
        break;
      case SourceType.ScheduleTemplates:
        this.sendScheduleTemplate();
        break;
      default:
        break;
    }
  }

  addToTable(): void {
    switch (this.sourceType) {
      case SourceType.HousePlans:
        this.onAddHousePlan();
        break;
      case SourceType.CSMaster:
        this.onAddCSMaster();
        break;
      case SourceType.JobSpecTemplates:
        this.onAddJobSpecTemplate();
        break;
      case SourceType.ScheduleTemplates:
        this.onAddScheduleTemplate();
        break;
      default:
        break;
    }
  }

  onAddCSMaster(): void {
    this.dialogService.openWithInput(colorSelectionMasterFinderDialogRoute, <CSMasterFinderDialogConfig>{
      preselected: this.cSMasterGridData.map(getCSMasterFinderListItemId),
      multiselection: true,
      sourceOfficeId: this.fromOffice.officeId,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((items: CSMasterFinderListItem[]) =>
        this.cSMasterGridData = this.cSMasterGridData.concat(items));
  }

  onAddHousePlan(): void {
    this.dialogService.openWithInput(housePlanFinderDialogRoute, <HousePlanFinderDialogConfig>{
      preselected: this.hpGridData.map(getHousePlanId),
      multiselection: true,
      sourceOfficeId: this.fromOffice.officeId,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((housePlans: HousePlanFinderListItem[]) =>
        this.hpGridData = this.hpGridData.concat(housePlans));
  }

  onAddJobSpecTemplate(): void {
    this.dialogService.openWithInput(jobSpecTemplateFinderDialogRoute, <JobSpecTemplateFinderDialogConfig>{
      preselected: this.jobSpecTemplateGridData.map(getJobSpecTemplateFinderListItemId),
      multiselection: true,
      sourceOfficeId: this.fromOffice.officeId,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((items: JobSpecTemplateFinderListItem[]) =>
        this.jobSpecTemplateGridData = this.jobSpecTemplateGridData.concat(items));
  }

  onAddScheduleTemplate(): void {
    this.dialogService.openWithInput(scheduleTemplateFinderDialogRoute, <ScheduleTemplateFinderDialogConfig>{
      preselected: this.scheduleTemplatesGridData.map(({ id }: ScheduleTemplateListItem) => id),
      multiselection: true,
      sourceOfficeId: this.fromOffice.officeId,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((scheduleTemplate: ScheduleTemplateListItem[]) =>
        this.scheduleTemplatesGridData = this.scheduleTemplatesGridData.concat(scheduleTemplate));
  }

  // @todo: what do the second filtering
  onTypeChange(type: SourceType): void {
    this.sourceType = type;
    switch (type) {
      case SourceType.CSMaster:
        this.cSMasterGridData = [];
        this.jobSpecTemplateGridData = [];
        break;
      case SourceType.HousePlans:
        this.hpGridData = [];
        this.jobSpecTemplateGridData = [];
        break;
      case SourceType.JobSpecTemplates:
        this.cSMasterGridData = [];
        this.hpGridData = [];
        break;
      case SourceType.ScheduleTemplates:
        this.scheduleTemplatesGridData = [];
        this.hpGridData = [];
        break;
      default:
        break;
    }
  }

  onRemoveHousePlan(housePlan: HousePlanFinderListItem): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_export_hp_delete', { code: housePlan.name })).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.hpGridData = this.hpGridData.filter((item) => item !== housePlan));
  }

  onRemoveCSMasterItem(finderItem: CSMasterFinderListItem): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_send_cs_delete', { name: finderItem.name })).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.cSMasterGridData = this.cSMasterGridData
        .filter((item) => item !== finderItem));
  }

  onRemoveJobSpecTemplateItem(finderItem: JobSpecTemplateFinderListItem): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_send_spec_delete', { name: finderItem.name })).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.jobSpecTemplateGridData = this.jobSpecTemplateGridData
        .filter((item) => item !== finderItem));
  }

  onRemoveScheduleTemplate(removingScheduleTemplate: ScheduleTemplateListItem): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_send_sch_delete', { name: removingScheduleTemplate.name })).pipe(
      filter$(yesAnswer))
      .subscribe(() =>
        this.scheduleTemplatesGridData = this.scheduleTemplatesGridData
          .filter((scheduleTemplate: ScheduleTemplateListItem) =>
            scheduleTemplate !== removingScheduleTemplate));
  }

  loadOffices($event: AutocompleteLoadMoreEvent): void {
    $event.load(this.businessService.findOfficesByName($event.query));
  }
}
